import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LANDING } from "@/constants/testIds";

// FAQ content sourced from emdpay.in (the brand's own FAQ page),
// lightly edited for clarity and tone.
const FAQS = [
  {
    q: "How can I pay my education or tuition fees using EMDpay?",
    a: "Log into EMDpay, choose the “Pay Tuition Fee” option, add the beneficiary's details (bank account, IFSC and other required info) and complete the payment using your preferred method — credit card, prepaid card, wallet, EMI or UPI.",
  },
  {
    q: "What are the benefits of paying tuition fees through EMDpay?",
    a: "Paying through EMDpay lets you earn credit card rewards, cashback and exclusive offers — along with the convenience of timely education payments and detailed transaction tracking.",
  },
  {
    q: "What types of education / tuition fee payments can I make?",
    a: "You can pay any beneficiary on the marketplace — including tutors, coaching institutes, schools, colleges, universities, and professional course providers.",
  },
  {
    q: "Can I pay education fees for any institution?",
    a: "Yes. EMDpay supports payments to any educational institution as long as you have their bank account details. The institution onboards as a verified seller once it accepts the invite and completes KYC.",
  },
  {
    q: "What are the benefits of paying via credit card on EMDpay?",
    a: "Credit-card payments on EMDpay let you earn rewards and cashback, use interest-free periods, and split large fees into smaller installments via EMI plans offered by your issuer.",
  },
  {
    q: "Is there a fee for paying education fees via credit card?",
    a: "Yes — a nominal processing fee is applied on payments made via credit card. The exact convenience fee is always shown upfront before you confirm the transaction.",
  },
  {
    q: "How long does it take for the payment to reach the institution?",
    a: "Payments are usually processed instantly or within 1–3 business days depending on the bank, KYC status of the seller, and standard banking cut-offs.",
  },
  {
    q: "Can I set up recurring payments for tuition fees?",
    a: "No — EMDpay currently does not allow you to schedule recurring payments for monthly or term-based fees. Each payment is initiated manually for security and clarity.",
  },
  {
    q: "Are payments made through EMDpay secure?",
    a: "Absolutely. EMDpay uses advanced encryption and secure payment gateways to keep your transactions and personal data safe.",
  },
  {
    q: "What should I do if my payment fails or is delayed?",
    a: "Reach out to EMDpay's customer support at support@emdpay.in — the team will assist with failed or delayed transactions and keep you posted until the issue is resolved.",
  },
];

export const FAQ = () => {
  return (
    <section
      id="faq"
      data-testid={LANDING.faqSection}
      className="relative py-24 md:py-32 bg-white border-y border-[#E5E5E0]"
    >
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
              · Frequently Asked
            </p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
              Answers,
              <br />
              <em className="italic text-[#F25C2A] font-normal">no fine print</em>.
            </h2>
            <p className="mt-5 text-[#4A4A4A] leading-relaxed">
              Can&apos;t find what you need? Email us at&nbsp;
              <a
                href="mailto:support@emdpay.in"
                className="text-[#0A0A0A] font-semibold underline underline-offset-4 decoration-[#F25C2A]"
              >
                support@emdpay.in
              </a>
              &nbsp;— our team replies within one business day.
            </p>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-[#E5E5E0] last:border-b-0"
                >
                  <AccordionTrigger
                    data-testid={`${LANDING.faqItem}-${i}`}
                    className="text-left font-serif text-lg sm:text-xl text-[#0A0A0A] py-6 hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A4A4A] text-base leading-relaxed pb-6 max-w-prose">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
